import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Switch, RouteComponentProps } from 'react-router-dom'

import './styles/Layout.scss'

import { AuthResponse } from 'panel/user/redux/types'
import { ActionResponse } from 'util/types/index'
import { ApplicationState } from 'AppReducer'
import { authenticateRefresh } from 'panel/user/redux/actions'
import { getPayload, getToken, handleRefreshToken, signOut } from 'util/services/AuthService'

// Root
import Alert from 'util/modules/Alert'

// Components
import Navbar from './components/Navbar'
import Sidebar from './components/Sidebar'
import Breadcrumb from './components/Breadcrumb'

import UserManager from 'panel/user/manager/UserManager'
import UserProfile from 'panel/user/profile/UserProfile'
import DocumentManager from 'panel/user/document/DocumentManager'
import ContactManager from 'panel/user/contact/ContactManager'
import AddressManager from 'panel/user/address/AddressManager'

import Category from 'panel/category/Category'
import Product from 'panel/product/Product'

import Partner from 'panel/partner/partner/Partner'
import PartnerConfig from 'panel/partner/config/PartnerConfig'
import ProductConfig from 'panel/partner/product-config/ProductConfig'

import Client from 'panel/client/Client'
import Order from 'panel/order/Order'
import Orders from 'panel/order/Orders'
import Balance from 'panel/balance/Balance'
import Notification from 'panel/notification/Notification'
import OrderStatus from 'panel/order-status/OrderStatus'

const Layout: React.FC<RouteComponentProps> = ({ match }) => {
  const dispatch = useDispatch()

  const [canRender, setCanRender] = useState(false)
  const [isSidebarOpen, setSidebarOpen] = useState(false)
  const logged = useSelector((state: ApplicationState) => state.userReducer.logged)

  const requestUpdateToken = useCallback(() => {
    const payload = getPayload()
    const { refreshToken } = getToken()
    if (!payload.sub || !refreshToken) throw new Error('Token não disponível')

    return authenticateRefresh({ id: payload.sub, token: refreshToken })(dispatch)
  }, [dispatch])

  async function updateStoredTokenAndIntervalUpdate (): Promise<ActionResponse<AuthResponse>> {
    return requestUpdateToken()
      .then((res) => {
        if (res?.status !== 'success') throw new Error()
        handleRefreshToken(requestUpdateToken)
        return res
      })
      .catch((err) => {
        signOut()
        throw new Error(`Usuário Não autenticado. ERROR: ${err.message}`)
      })
  }

  // Public Fetch
  useEffect(() => {
    ;(async (): Promise<void> => {
      setCanRender(false)
      await updateStoredTokenAndIntervalUpdate()
        .catch((err) => {
          signOut()
          console.log(err.message)
        })
      setCanRender(true)
    })()
    // eslint-disable-next-line
  }, [])

  // Private Fetch
  useEffect(() => {
    if (logged) {

    }
  }, [logged, dispatch])

  if (!canRender) return null

  return (
    <>
      <Alert />
      <div className="row layout-panel">
        <Navbar openSidebar={(): void => setSidebarOpen(true)} />
        <Sidebar isOpen={isSidebarOpen} closeSidebar={(): void => setSidebarOpen(false)} />
        <div className="content">
          <Breadcrumb />

          <Switch>
            <Route exact path={`${match.url}/partner/:id?`} component={Partner} />
            <Route path={`${match.url}/user/:id?`} component={UserManager} />
            <Route exact path={`${match.url}/profile`} component={UserProfile} />
            <Route
              exact
              path={[
                `${match.url}/profile/address/:addressId/edit`,
                `${match.url}/profile/contact/:contactId/edit`,
                `${match.url}/profile/document/:documentId/edit`
              ]}
              component={UserProfile}
            />
            <Route
              exact
              path={[
                `${match.url}/person/:personId/document`,
                `${match.url}/person/:personId/document/:documentId/edit`
              ]}
              component={DocumentManager}
            />
            <Route
              exact
              path={[
                `${match.url}/person/:personId/contact`,
                `${match.url}/person/:personId/contact/:contactId/edit`
              ]}
              component={ContactManager}
            />
            <Route
              exact
              path={[
                `${match.url}/person/:personId/address`,
                `${match.url}/person/:personId/address/:addressId/edit`
              ]}
              component={AddressManager}
            />

            <Route exact path={`${match.url}/partner/:partnerId/product/:id?`} component={Product} />
            <Route
              exact
              path={`${match.url}/partner/:partnerId/product/:id/config`}
              component={ProductConfig}
            />

            <Route exact path={`${match.url}/partner/:id/config`} component={PartnerConfig} />
            <Route exact path={`${match.url}/client`} component={Client} />
            <Route exact path={`${match.url}/order`} component={Orders} />
            <Route exact path={`${match.url}/order/:partnerId([0-9]+)/:orderId([0-9]+)`} component={Order} />
            <Route exact path={`${match.url}/balance/:id?`} component={Balance} />
            <Route exact path={`${match.url}/notification`} component={Notification} />

            <Route path={`${match.url}/category/:id?`} component={Category} />
            <Route path={`${match.url}/order/status/:id?`} component={OrderStatus} />
          </Switch>
        </div>
      </div>
    </>
  )
}

export default Layout
